import React, { useEffect, useState } from "react";
import { BookmarkPlus, Check, EyeOff } from "lucide-react";
import { MenuDivider, MenuItem } from "@/components/ui";
import { useAuthedApi } from "@/api/useAuthedApi";
import type { CaptureState } from "@/api/conversationKnowledgeApi";

/** The chat menu's conversation-knowledge items (spec §7.3): "Save to
 *  knowledge…" and the per-conversation "Don't save this conversation"
 *  toggle. Render only when the feature is on. Both disappear when the
 *  capture read 404s: the conversation is not the caller's (or not stored
 *  yet), so a save would 404 too. */
export function ConversationKnowledgeMenuItems({
  sessionId,
  onSave,
}: {
  sessionId: string;
  onSave: () => void;
}) {
  const { conversationKnowledge } = useAuthedApi();
  const [capture, setCapture] = useState<CaptureState | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCapture(null);
    setHidden(false);
    setError(null);
    conversationKnowledge.getCapture(sessionId).then(
      (c) => {
        if (cancelled) return;
        // null: the feature is off, or the conversation is not the caller's.
        if (c === null) setHidden(true);
        else setCapture(c);
      },
      (e) => {
        if (!cancelled) console.warn("capture state read failed", e);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [conversationKnowledge, sessionId]);

  if (hidden) return null;

  const toggle = async () => {
    if (!capture || busy) return;
    setBusy(true);
    setError(null);
    try {
      setCapture(await conversationKnowledge.setCapture(sessionId, !capture.enabled));
    } catch (e) {
      console.warn("capture toggle failed", e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const optedOut = capture ? !capture.enabled : false;
  return (
    <>
      <MenuDivider />
      <MenuItem icon={<BookmarkPlus size={14} />} data-ph="save_knowledge" onClick={onSave}>
        Save to knowledge…
      </MenuItem>
      {capture && (
        <MenuItem
          role="menuitemcheckbox"
          aria-checked={optedOut}
          disabled={busy}
          icon={optedOut ? <Check size={14} /> : <EyeOff size={14} />}
          data-ph="toggle_capture"
          onClick={() => void toggle()}
        >
          Don&apos;t save this conversation
        </MenuItem>
      )}
      {error && (
        <p role="alert" className="m-0 px-3 py-1.5 text-[12.5px] text-danger max-w-[240px]">
          {error}
        </p>
      )}
    </>
  );
}
